/**
 * Backup key verification utility for Matrix key backup (Rust crypto first).
 */
import matrixService from '../../services/matrixService';
import secretStorageService from '../../services/SecretStorageService';
import logger from '../../utils/logger';

/**
 * Verifies that a recovery key can decrypt the backup using the Rust crypto APIs.
 * Falls back to legacy client APIs only if necessary.
 *
 * @param client - Matrix client instance
 * @param recoveryKeyBytes - Recovery key as raw bytes (Uint8Array length 32)
 * @returns Promise<boolean> true if the key matches the server backup
 * @throws Error with code 'CRYPTO_NOT_READY' or 'SDK_UNAVAILABLE' if APIs are missing
 * @throws Error with code 'NO_BACKUP' if no backup exists
 * @throws Error with code 'VERIFY_FAILED' for unexpected errors
 */

export async function verifyBackupKey(client, recoveryKeyBytes) {
  // Set the temporary verification key before any operations
  logger.debug('RECOVERY verifyBackupKey: Setting temporary verification key, bytes:', recoveryKeyBytes?.length);
  matrixService.setTemporaryVerificationKey(recoveryKeyBytes);

  try {
    const crypto = typeof client.getCrypto === 'function' ? client.getCrypto() : null;

    // ELEMENT WEB FLOW CORRECTION
    // Element uses the recovery key to unlock Secret Storage (SSSS) and obtain the
    // megolm backup private key (secret: m.megolm_backup.v1). We will attempt this
    // before trying to use the provided bytes directly as a backup private key.

    // Check device verification status FIRST
    try {
      const userId = client.getUserId();
      const deviceId = client.getDeviceId();

      // Check if device is verified
      const deviceTrust = await crypto.getDeviceVerificationStatus(userId, deviceId);

      // Check cross-signing status
      const crossSigningStatus = await crypto.getCrossSigningStatus();

      // Check if we need to bootstrap cross-signing
      const userTrust = await crypto.getUserVerificationStatus(userId);

      if (deviceTrust && !deviceTrust.isVerified?.()) {
        // Device is NOT verified - may prevent backup key from working correctly
      }
    } catch (e) {
      // Could not check device trust
    }

    // First check if we can access the backup decryptor to validate the key
    // This mimics what Element Web does - validate BEFORE trying to restore

    // Rust crypto path (preferred)
    if (crypto && typeof crypto.getKeyBackupInfo === 'function') {
      try {
        const info = await crypto.getKeyBackupInfo();

        // Try to unlock SSSS and fetch the backup private key (preferred path)
        let backupPrivateKeyBytes = null;
        const ssssBootstrapped = false;

        try {
          // Try to open SSSS with the recovery key
          logger.debug('RECOVERY verifyBackupKey: Attempting to open SSSS with recovery key');
          await secretStorageService.openWithRecoveryKey(recoveryKeyBytes);

          // Now try to read the backup key from SSSS
          try {
            logger.debug('RECOVERY verifyBackupKey: Reading megolm backup key from SSSS');
            const backupKeyB64 = await secretStorageService.getSecret('m.megolm_backup.v1');

            if (backupKeyB64) {
              const norm = (s) => (s || '').replace(/-/g, '+').replace(/_/g, '/');
              const withPad = (s) => s + '='.repeat((4 - (s.length % 4)) % 4);
              const b64 = withPad(norm(backupKeyB64));
              const raw = (typeof atob === 'function')
                ? Uint8Array.from(atob(b64), c => c.charCodeAt(0))
                : Uint8Array.from(Buffer.from(b64, 'base64'));
              backupPrivateKeyBytes = raw;
              logger.debug('RECOVERY verifyBackupKey: Got backup private key from SSSS, bytes:', raw?.length);
            }
          } catch (readErr) {
            // Could not read from SSSS
            logger.debug('RECOVERY verifyBackupKey: Could not read backup key from SSSS:', readErr?.message);
          }
        } catch (e) {
          // SSSS operations failed
          logger.debug('RECOVERY verifyBackupKey: SSSS operations failed:', e?.message);
        }

        if (backupPrivateKeyBytes && backupPrivateKeyBytes.length === 32) {
          try {
            logger.debug('RECOVERY verifyBackupKey: Trying SSSS-derived key with backup');
            const decryptor = await crypto.getBackupDecryptor(info, backupPrivateKeyBytes);
            if (decryptor?.free) decryptor.free();
            logger.debug('RECOVERY verifyBackupKey: Storing backup private key and restoring');
            await crypto.storeSessionBackupPrivateKey(backupPrivateKeyBytes, info.version);
            await crypto.restoreKeyBackup();
            logger.debug('RECOVERY verifyBackupKey: ✅ SUCCESS with SSSS-derived key');
            return true;
          } catch (e) {
            // SSSS-derived key did not validate against backup
            // Continue with direct validation fallback below
            logger.debug('RECOVERY verifyBackupKey: SSSS-derived key failed:', e?.message);
          }
        }

        // Try to get ALL backup versions from server
        try {
          // Direct API call to get backup versions
          const versions = await client.http.authedRequest(
            'GET',
            '/room_keys/version',
            undefined,
            undefined,
            { prefix: '/_matrix/client/v3' },
          );
        } catch (e) {
          // Could not fetch backup versions
        }

        // Try to check if there are multiple backup versions
        try {
          const backupEnabled = await crypto.isKeyBackupEnabled();

          // Get active backup info
          const activeBackup = await crypto.getActiveSessionBackupVersion();
        } catch (e) {
          // Could not check backup versions
        }
        if (!info || !info.version) {
          const e = new Error('NO_BACKUP');
          e.code = 'NO_BACKUP';
          throw e;
        }

        // Element Web approach: First check trust chain

        // Check current backup trust status - but handle API differences
        let trustInfo = null;
        try {
          trustInfo = await crypto.isKeyBackupTrusted(info);
        } catch (e) {
          // Could not check backup trust
        }


        // Try the direct verification flow only if SSSS path failed
        // This handles legacy setups where recovery key IS the backup key
        try {
          logger.debug('RECOVERY verifyBackupKey: Trying direct validation with recovery key');
          const backupDecryptor = await crypto.getBackupDecryptor(info, recoveryKeyBytes);

          // Clean up the decryptor
          if (backupDecryptor && typeof backupDecryptor.free === 'function') {
            backupDecryptor.free();
          }

          // Store the key for backup restoration
          await crypto.storeSessionBackupPrivateKey(recoveryKeyBytes, info.version);

          // If we bootstrapped SSSS, store the backup key there for future use
          if (ssssBootstrapped) {
            try {
              const secretStorage = client.getSecretStorage?.();
              const keyB64 = btoa(String.fromCharCode(...recoveryKeyBytes));
              await secretStorage?.store?.('m.megolm_backup.v1', keyB64);
            } catch (storeErr) {
              // Could not store in SSSS
            }
          }

          // Bootstrap cross-signing if needed
          try {
            const xsignStatus = await crypto.getCrossSigningStatus();
            if (!xsignStatus?.publicKeysOnDevice || !xsignStatus?.privateKeysCachedLocally?.masterKey) {
              await crypto.bootstrapCrossSigning({
                authUploadDeviceSigningKeys: async (makeRequest) => makeRequest(),
                setupNewCrossSigning: !xsignStatus?.publicKeysOnDevice,
              });
            }
          } catch (xsignErr) {
            // Cross-signing bootstrap failed (non-fatal)
          }

          await crypto.restoreKeyBackup();
          logger.debug('RECOVERY verifyBackupKey: ✅ SUCCESS with direct recovery key');
          return true;

        } catch (err) {
          logger.debug('RECOVERY verifyBackupKey: Direct validation failed:', err?.message);
          const msg = err?.message || '';
          if (msg.includes('does not match the decryption key') || msg.includes('M_WRONG_KEY')) {
            logger.debug('RECOVERY verifyBackupKey: Key mismatch error detected');
            // Check if we should offer to reset the backup
            if (ssssBootstrapped) {
              // Since SSSS was bootstrapped, you could:
              // - Reset the backup and create a new one
              // - This would lose existing message history
            }

            return false;
          }
          // If network or other issues occur, propagate a generic verification failure
          const e = new Error('VERIFY_FAILED');
          e.code = 'VERIFY_FAILED';
          e.details = err;
          throw e;
        }
      } catch (err) {
        // Only throw CRYPTO_NOT_READY for actual missing API
        if (err?.message?.includes('not a function') || err?.message?.includes('undefined')) {
          const e = new Error('CRYPTO_NOT_READY');
          e.code = 'CRYPTO_NOT_READY';
          e.details = err;
          throw e;
        }

        // For other errors, rethrow
        if (err?.code) throw err;
        throw err;
      }
    }

    // Legacy client API fallback (keeps previous behaviour for tests and older SDKs)
    try {
      const cryptoApi = await import('matrix-js-sdk/lib/crypto-api');
      if (!cryptoApi.encodeRecoveryKey || typeof client.restoreKeyBackupWithRecoveryKey !== 'function') {
        const e = new Error('SDK_UNAVAILABLE');
        e.code = 'SDK_UNAVAILABLE';
        throw e;
      }

      // Convert bytes to SDK recovery key string
      const keyBytesCopy = new Uint8Array(recoveryKeyBytes);
      const recoveryKey = cryptoApi.encodeRecoveryKey(keyBytesCopy);

      // Follow legacy flow: gate on enabled + version
      const isEnabled = typeof client.isKeyBackupEnabled === 'function'
        ? await client.isKeyBackupEnabled()
        : true;
      if (!isEnabled) {
        return false;
      }

      const backupVersion = typeof client.getKeyBackupVersion === 'function'
        ? await client.getKeyBackupVersion()
        : null;
      if (!backupVersion) {
        return false;
      }

      // Prepare early-abort progress behavior as before
      let decryptedAny = false;
      let abortController;
      if (typeof AbortController !== 'undefined') {
        abortController = new AbortController();
      }

      const ABORT_EARLY = new Error('ABORT_EARLY');
      const options = {
        progressCallback: () => {
          decryptedAny = true;
          if (abortController) abortController.abort();
          throw ABORT_EARLY;
        },
      };
      if (abortController) options.abortSignal = abortController.signal;

      try {
        await client.restoreKeyBackupWithRecoveryKey(recoveryKey, backupVersion, options);
        return true;
      } catch (err) {
        if ((err && err.message === 'ABORT_EARLY') && decryptedAny) return true;
        if (err?.name === 'AbortError' && decryptedAny) return true;
        const msg = err?.message || '';
        if (err?.errcode === 'M_WRONG_KEY' || err?.code === 'M_WRONG_KEY') {
          return false;
        }
        if (err?.name && err.name.includes('KeyMismatch')) return false;
        if (msg.includes('M_WRONG_KEY') || msg.includes('KeyMismatch') || msg.includes('does not match')) {
          return false;
        }
        const e = new Error('VERIFY_FAILED');
        e.code = 'VERIFY_FAILED';
        e.details = err;
        throw e;
      } finally {
        // Zeroize
        keyBytesCopy.fill(0);
      }
    } catch (err) {
      const msg = err?.message || '';
      if (msg.includes('M_WRONG_KEY') || msg.includes('does not match')) {
        // Clear the temporary key on verification failure
        matrixService.clearTemporaryVerificationKey();
        return false;
      }
      if (err?.code) {
        // Clear the temporary key before re-throwing
        matrixService.clearTemporaryVerificationKey();
        throw err;
      }
      const e = new Error('VERIFY_FAILED');
      e.code = 'VERIFY_FAILED';
      e.details = err;
      // Clear the temporary key before throwing
      matrixService.clearTemporaryVerificationKey();
      throw e;
    }
  } finally {
    // Always clean up the temporary verification key
    matrixService.clearTemporaryVerificationKey();
  }
}